import { RootState, ThunkDispatch } from "core-integration/src/store";
import { fetchEventLive } from "core-integration/src/store/elements/thunks";
import { getEntryEventPoints } from "core-integration/src/store/entries/reducers";
import { fetchEntryEventPicks } from "core-integration/src/store/entries/thunks";
import { getNextEvent } from "core-integration/src/store/events/reducers";
import type { EntryCrestData } from "core-integration/src/store/ui";
import { Button } from "plos/src/components/buttons/Button";
import { iconStyles } from "plos/src/components/HeadingInContainer/headingInContainer.css";
import ButtonLink from "plos/src/components/links/ButtonLink";
import Link from "plos/src/components/links/Link";
import AiIcon from "plos/src/img/icons/ai.svg?react";
import RightArrow from "plos/src/img/icons/right-arrow.svg?react";
import ShirtIcon from "plos/src/img/icons/shirt.svg?react";
import TransfersIcon from "plos/src/img/icons/transfers.svg?react";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useAdobeContext } from "../../../contexts/AdobeContext";
import { useTrackingContext } from "../../../contexts/TrackingContext";
import { EntryCrest } from "../../crests/EntryCrest";
import EntryHeading from "./EntryHeading";
import {
  arrowIconStyles,
  buttonContainer,
  eventName,
  eventSummaryContainer,
  eventSummaryIcon,
  eventSummaryScorePanel,
  eventSummaryScores,
  eventSummaryWrap,
  glassDivider,
  gradientWrapper,
  linkStyles,
  primaryScore,
  scoreHeaders,
  secondaryScore,
  statusSummaryHeader,
} from "./eventSummary.css";
import { EventSummaryProps } from "./types";

const EventSummary = ({
  adobeGameweekTemplateUrl,
  adobeSeasonTemplateUrl,
  entry,
  currentEvent,
}: EventSummaryProps) => {
  const { embed } = useAdobeContext();
  const { fireClickTrackEvent } = useTrackingContext();
  const dispatch = useDispatch<ThunkDispatch>();

  const { t } = useTranslation("status");

  const trackSummaryClick = (
    eventDetail:
      | "Pick Team"
      | "Transfers"
      | "View Gameweek Recap"
      | "View Season Recap"
      | "View Team Details",
  ) => {
    fireClickTrackEvent({
      event_component: "button click",
      event_category: "fantasy team summary",
      event_detail: eventDetail,
    });
  };

  const { id: entryId, started_event: startedEvent } = entry;

  const {
    id: currentEventId,
    highest_scoring_entry: highestScoringEntry,
    highest_score: highestScore,
    average_entry_score: averageEntryScore,
    name: currentEventName,
  } = currentEvent;
  const nextEvent = useSelector(getNextEvent);

  const hasJoinedCurrentEvent = startedEvent <= currentEventId;

  const entryPoints = useSelector((state: RootState) =>
    getEntryEventPoints(state, entryId, currentEventId),
  );
  const entryCrestData: EntryCrestData = {
    id: entry.id,
    name: entry.name,
    club_badge_src: entry.club_badge_src,
  };

  const fetchData = useCallback(() => {
    // if the entry joined after the current event deadline
    // no picks will be available
    if (hasJoinedCurrentEvent) {
      dispatch(fetchEntryEventPicks(entryId, currentEventId));
      dispatch(fetchEventLive(currentEventId));
    }
  }, [dispatch, entryId, currentEventId, hasJoinedCurrentEvent]);

  useEffect(() => {
    if (entryId && currentEventId) {
      fetchData();
    }
  }, [dispatch, fetchData, entryId, currentEventId]);

  return (
    <div className={gradientWrapper}>
      <div className={eventSummaryContainer}>
        <div className={statusSummaryHeader}>
          <EntryCrest entryCrestData={entryCrestData} dimension={35} />
          {hasJoinedCurrentEvent ? (
            <Link
              to={`/entry/${entryId}/event/${currentEventId}`}
              className={linkStyles}
              onClick={() => trackSummaryClick("View Team Details")}
            >
              <EntryHeading entry={entry} />
              <RightArrow width={24} height={24} className={iconStyles} />
            </Link>
          ) : (
            <EntryHeading entry={entry} />
          )}
        </div>
        <hr className={glassDivider} />

        <div className={eventSummaryWrap}>
          <h3 className={eventName}>{currentEventName}</h3>

          <div className={eventSummaryScores}>
            {/* Average score panel */}
            <div className={eventSummaryScorePanel}>
              <h4 className={scoreHeaders}>{t("average", "Average")}</h4>
              <div className={secondaryScore}>{averageEntryScore}</div>
            </div>
            {/* Entry score panel */}
            <div className={eventSummaryScorePanel}>
              {hasJoinedCurrentEvent ? (
                <>
                  <Link
                    to={`/entry/${entryId}/event/${currentEventId}`}
                    className={linkStyles}
                  >
                    {t("points", "Points")}
                    <RightArrow
                      width={12}
                      height={12}
                      className={arrowIconStyles}
                    />
                  </Link>
                  <h4 className={primaryScore}>{entryPoints}</h4>
                </>
              ) : (
                <h4 className={primaryScore}>-</h4>
              )}
            </div>
            {/* Highest score panel */}
            <div className={eventSummaryScorePanel}>
              {highestScore ? (
                <Link
                  to={`/entry/${highestScoringEntry}/event/${currentEventId}`}
                  className={linkStyles}
                >
                  {t("highest", "Highest")}
                  <RightArrow
                    width={12}
                    height={12}
                    className={arrowIconStyles}
                  />
                </Link>
              ) : (
                <h4 className={scoreHeaders}>{t("highest", "Highest")}</h4>
              )}
              <span className={secondaryScore}>
                {highestScore ? highestScore : "-"}
              </span>
            </div>
          </div>

          <div className={buttonContainer}>
            {nextEvent && (
              <ButtonLink
                appearance="glass"
                fullWidth
                to="/my-team"
                onClick={() => trackSummaryClick("Pick Team")}
              >
                <ShirtIcon className={eventSummaryIcon} />
                {t("pickTeam", "Pick Team")}
              </ButtonLink>
            )}
            <ButtonLink
              appearance="glass"
              fullWidth
              to="/transfers"
              onClick={() => trackSummaryClick("Transfers")}
            >
              <TransfersIcon className={eventSummaryIcon} />
              {t("transfers", "Transfers")}
            </ButtonLink>
            {adobeGameweekTemplateUrl && (
              <Button
                appearance="glass"
                fullWidth
                onPress={() => {
                  trackSummaryClick("View Gameweek Recap");
                  embed.viewImage({
                    badgeHolder: Boolean(
                      entry.club_badge_src &&
                      entry.club_badge_src !== "Pending",
                    ),
                    entryPoint: "persistent-cta",
                    url: adobeGameweekTemplateUrl,
                    type: "gameweek",
                  });
                }}
              >
                <AiIcon className={eventSummaryIcon} />
                {t("common:viewGameweekRecap", "View Gameweek Recap")}
              </Button>
            )}
            {adobeSeasonTemplateUrl && (
              <Button
                appearance="glass"
                fullWidth
                onPress={() => {
                  trackSummaryClick("View Season Recap");
                  embed.viewImage({
                    badgeHolder: Boolean(
                      entry.club_badge_src &&
                      entry.club_badge_src !== "Pending",
                    ),
                    entryPoint: "persistent-cta",
                    url: adobeSeasonTemplateUrl,
                    type: "season",
                  });
                }}
              >
                <AiIcon className={eventSummaryIcon} />
                {t("common:viewSeasonRecap", "View Season Recap")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventSummary;
